<template>
  <dl class="-mx-5 bg-background-light px-5 pb-8 pt-4 sm:-mx-8 sm:px-8 md:mx-0 md:rounded-2xl md:bg-transparent md:px-6 md:pt-10">
    <CollapsibleRoot class="mb-4">
      <div class="flex justify-between gap-2">
        <h4>{{ t['w.basket.summary']() }}</h4>
        <CollapsibleTrigger class="shrink-0 text-base text-text-strong underline hover:no-underline">{{ productsInBasketLabel }}</CollapsibleTrigger>
      </div>
      <CollapsibleContent ref="basketItemsCollapsible" class="overflow-hidden data-[state=closed]:animate-collapsible-close data-[state=open]:animate-collapsible-open">
        <div ref="basketItemsContainer">
          <div class="grid grid-cols-[1fr_auto] items-center gap-2 pt-2">
            <template v-for="(item, index) in props.items" :key="index">
              <span class="text-sm">
                <span class="font-medium">{{ t['w.global.product_quantity_text']({ quantity: item.quantity }) }}</span>
                {{ item.name }}
              </span>
              <span class="justify-self-end text-base">
                <Price :amount="(item.discountPrice ?? item.regularPrice) * item.quantity" />
              </span>
            </template>
          </div>
        </div>
      </CollapsibleContent>
    </CollapsibleRoot>

    <div class="pb-2">
      <div class="flex items-center justify-between">
        <dt class="overflow-hidden text-ellipsis text-base">{{ t['w.basket.summary.regular_price']() }}</dt>
        <div v-if="discountPriceSum !== regularPriceSum" class="ml-auto flex items-center pl-4">
          <Price :amount="regularPriceSum" large-fractional class="mr-1.5 text-lg text-text-light line-through" />
          <Price :amount="discountPriceSum" class="text-lg" />
        </div>
        <Price v-else :amount="regularPriceSum" class="ml-auto pl-4 text-lg" />
      </div>
      <div v-if="customerDiscountAmount > 0" class="mt-2 flex items-center justify-between text-text-strong">
        <div class="flex items-center">
          <dt class="overflow-hidden text-ellipsis text-base">{{ user ? t['w.basket.summary.customer_credits']() : t['w.basket.summary.customer_price']() }}</dt>
          <InfoTooltip v-if="user" class="ml-1.5">
            <CustomerCreditExplanation :items="props.items" :promo-code-discount="promoCodeDiscount" />
          </InfoTooltip>
        </div>
        <Price :amount="user ? -customerDiscountAmount : customerDiscountPriceSum" class="text-lg" />
      </div>
    </div>
    <div v-if="promoCode" class="pb-2">
      <div class="flex items-center justify-between">
        <div class="flex items-center text-base">
          {{ t['w.ostukorv.soodustus.promo_code']() }}
          {{ promoCode.name?.toUpperCase() }}
          <InfoTooltip class="ml-1">{{ t['w.basket.summary.voucher_tooltip']() }}</InfoTooltip>
          <button v-if="canEditVoucher" class="ml-2 text-sm text-text-light underline hover:no-underline" type="button" @click="removeVoucher">
            {{ t['w.basket.summary.voucher_remove']() }}
          </button>
        </div>
        <Price :amount="-promoCodeDiscount" class="text-lg" />
      </div>
    </div>
    <VoucherField v-if="canEditVoucher" ref="voucherFieldRef" v-model:promo-code="promoCode" />
    <div v-if="transportSum !== null" class="flex items-center justify-between pb-2">
      <dt class="overflow-hidden text-ellipsis text-base">{{ transportLabel }}</dt>
      <div class="ml-auto flex items-center pl-4">
        <Price :amount="transportSum" class="ml-auto pl-4 text-lg" />
      </div>
    </div>
    <div class="mt-2 border-t border-border-medium py-4 md:py-6">
      <div v-if="totalSavedSum && totalSavedSum !== 0" class="mb-3 flex items-center justify-between">
        <div class="flex">
          <dt class="overflow-hidden text-ellipsis text-sm">
            {{ t['w.basket.summary.total_saved']({ percent: totalDiscountPercentLabel }) }}
          </dt>
        </div>
        <Price :amount="totalSavedSum" class="text-base" />
      </div>
      <div v-if="potentialCustomerPriceSavings && potentialCustomerPriceSavings !== 0" class="mb-3 flex items-center justify-between text-text-strong">
        <div class="flex">
          <dt class="overflow-hidden text-ellipsis text-sm">
            {{ t['w.basket.summary.customer_potential_savings']() }}
          </dt>
        </div>
        <Price :amount="potentialCustomerPriceSavings" class="text-base" />
      </div>
      <div v-if="countryId === 2 && props.vat" class="mb-3 flex items-center justify-between">
        <div class="flex">
          <dt class="overflow-hidden text-ellipsis text-sm">{{ t['w.basket.summary.without_tax']({ vat: props.vat.toLocaleString('et-EE') }) }}</dt>
        </div>
        <Price :amount="finalSumWithoutTax" class="text-base" />
      </div>
      <div class="flex items-center justify-between">
        <div class="flex">
          <dt class="overflow-hidden text-ellipsis text-base">
            <span class="hidden md:inline">{{ t['w.basket.summary.total_price']() }}</span>
            <span class="md:hidden">{{ t['w.basket.summary.total_price_mobile']() }}</span>
          </dt>
        </div>
        <Price :amount="finalSum" class="text-subtitle" />
      </div>
    </div>
    <div v-if="props.hasBottom" class="relative mt-8">
      <Button
        v-if="user || !props.loginPrompt"
        class="btn-rounded w-full"
        type="submit"
        :icon-name="props.allowSubmit ? 'arrow-right' : null"
        :disabled="!props.allowSubmit"
        :is-loading="props.isLoading"
      >
        <span class="hidden md:inline">{{ t['w.basket.continue']() }}</span>
        <span class="md:hidden">{{ t['w.basket.continue_mobile']() }}</span>
      </Button>
      <template v-else>
        <Button class="btn-rounded mb-4 w-full" icon-name="arrow-right" @click.prevent="loginModalStore.open(t['w.login.welcome']())">
          <span class="hidden md:inline">{{ t['w.basket.continue_as_client']() }}</span>
          <span class="md:hidden">{{ t['w.basket.continue_as_client_mobile']() }}</span>
        </Button>
        <Button class="btn-rounded btn-secondary w-full" type="submit" :is-loading="props.isLoading">
          <span class="hidden md:inline">{{ t['w.basket.continue_as_guest']() }}</span>
          <span class="md:hidden">{{ t['w.basket.continue_as_guest_mobile']() }}</span>
        </Button>
      </template>
    </div>
  </dl>
</template>

<script setup lang="ts">
import * as t from '~/messages'
import type { IBasketItem, IVoucher } from '~/components/order/BasketTypes'
import Price from '~/components/price/Price.vue'
import CustomerCreditExplanation from '~/components/order/CustomerCreditExplanation.vue'
import { CollapsibleContent, CollapsibleRoot, CollapsibleTrigger } from 'reka-ui'
import { useBasketState } from '~/composables/states'
import VoucherField from '~/components/order/VoucherField.vue'
import { useLoginModalStore } from '~/stores/login-modal'
import { useResizeObserver } from '@vueuse/core'

const user = useUserState()
const basket = useBasketState()

const basketItemsCollapsible = useTemplateRef('basketItemsCollapsible')
const basketItemsContainer = useTemplateRef('basketItemsContainer')

const {
  public: { countryId },
} = useRuntimeConfig()

const productsInBasketLabel = computed(() => {
  if (basket.value > 1) {
    return t['w.basket.products_in_basket_plural']({ quantity: basket.value })
  }

  return t['w.basket.products_in_basket']({ quantity: basket.value })
})

const props = withDefaults(
  defineProps<{
    items: IBasketItem[]
    transportType?: number | null
    transportOptions?: any[] | null
    allowSubmit?: boolean
    hasBottom?: boolean
    isTransportPriceConfirmed?: boolean
    loginPrompt?: boolean
    isLoading?: boolean
    canEditVoucher?: boolean
    promoCodeDiscount: number
    vat?: number | null
  }>(),
  {
    allowSubmit: true,
    hasBottom: false,
    isTransportPriceConfirmed: true,
    loginPrompt: false,
    transportType: null,
    transportOptions: null,
    isLoading: false,
    canEditVoucher: false,
    vat: null,
  }
)

const promoCode = defineModel<IVoucher | null>('promoCode', { required: true })

const voucherFieldRef = useTemplateRef('voucherFieldRef')

const loginModalStore = useLoginModalStore()

const transportSum = computed(() => {
  if (!props.transportType || !props.transportOptions) {
    return null
  }

  if (!props.isTransportPriceConfirmed) {
    return null
  }

  const selectedTransport = props.transportType
  const transportOption = props.transportOptions.find((opt) => opt.id === selectedTransport)

  if (transportOption) {
    if (transportOption.transport0Threshold !== null) {
      if (transportOption.transport0Threshold <= discountPriceSum.value) {
        return 0
      }
    }

    return transportOption.price
  }

  return 0
})

const transportLabel = computed(() => {
  if (!props.transportType || !props.transportOptions) {
    return t['w.basket.summary.transport']()
  }

  const selectedTransport = props.transportType
  const transportOption = props.transportOptions.find((opt) => opt.id === selectedTransport)

  if (!transportOption) {
    return t['w.basket.summary.transport']()
  }

  if (transportOption.transport0Threshold !== null) {
    if (transportOption.transport0Threshold <= discountPriceSum.value) {
      return 0
    }
  }

  return transportOption.label ?? t['w.basket.summary.transport']()
})

const regularPriceSum = computed(() => {
  if (!props.items || props.items.length < 1) {
    return 0
  }

  return props.items.reduce((accumulator, currentValue) => {
    return accumulator + currentValue.quantity * currentValue.regularPrice
  }, 0)
})

const discountPriceSum = computed(() => {
  if (!props.items || props.items.length < 1) {
    return 0
  }

  return props.items.reduce((accumulator, currentValue) => {
    return accumulator + currentValue.quantity * (currentValue.discountPrice ?? currentValue.regularPrice)
  }, 0)
})

const itemSum = computed(() => {
  if (!props.items || props.items.length < 1) {
    return 0
  }

  return props.items.reduce((accumulator, currentValue) => {
    let productPrice = currentValue.discountPrice ?? currentValue.regularPrice

    if (promoCode.value !== null) {
      /* if voucher used and discount is bigger than product discount - use regular product price for calculations */
      const productDefaultDiscount = Math.round((1 - (currentValue.discountPrice ?? currentValue.regularPrice) / currentValue.regularPrice) * 100)
      productPrice = promoCode.value.discountPercentage > productDefaultDiscount ? currentValue.regularPrice : (currentValue.discountPrice ?? currentValue.regularPrice)
    }

    return accumulator + currentValue.quantity * productPrice
  }, 0)
})

const potentialCustomerPriceSavings = computed(() => {
  if (!props.items || props.items.length < 1) {
    return 0 // no items in cart
  }

  if (user.value) {
    return 0 // only relevant for logged-out users
  }

  return props.items.reduce((accumulator, currentValue) => {
    if (currentValue.customerPrice != null && currentValue.customerPrice > 0) {
      const regularUserPrice = currentValue.discountPrice ?? currentValue.regularPrice
      const customerSavingsPerItem = regularUserPrice - currentValue.customerPrice

      return accumulator + Math.max(customerSavingsPerItem, 0) * currentValue.quantity
    }
    return accumulator
  }, 0)
})

const priceSum = computed(() => {
  if (!props.items || props.items.length < 1) {
    return 0
  }

  if (user.value) {
    const userBalance = user.value.balance < 0 ? 0 : user.value.balance // should not happen, but if we have made a mistake with users balances - front-end shouldn't calculate with negative numbers (back-end ignores them)

    return itemSum.value - Math.min(userBalance, customerDiscountPriceSum.value)
  }

  return itemSum.value
})

const finalSum = computed(() => priceSum.value - props.promoCodeDiscount + transportSum.value)

const finalSumWithoutTax = computed(() => finalSum.value / (1 + (props.vat ?? 0) / 100))

/* cart total price with customer discounts */
const customerDiscountPriceSum = computed(() => {
  if (!props.items || props.items.length < 1) {
    return 0
  }

  const sum = props.items.reduce((accumulator, currentValue) => {
    const regularUserPrice = currentValue.discountPrice ?? currentValue.regularPrice
    return accumulator + currentValue.quantity * (currentValue.customerPrice ? regularUserPrice - currentValue.customerPrice : 0)
  }, 0)

  // FIXME: this part is still broken because it does calculations for whole cart, but we can have different item based discounts (one has promo, one has customer price)
  if (props.promoCodeDiscount > sum) {
    return 0
  }

  return Math.round(sum * 100) / 100
})

const totalDiscountPercent = computed(() => 1 - (finalSum.value - transportSum.value) / regularPriceSum.value)

const totalDiscountPercentLabel = computed(() => Math.floor(totalDiscountPercent.value * 100).toString() + '%')

const totalSavedSum = computed(() => finalSum.value - transportSum.value - regularPriceSum.value)

const removeVoucher = () => {
  voucherFieldRef.value?.clear()
}

const customerDiscountAmount = computed(() => {
  return discountPriceSum.value - priceSum.value
})

useResizeObserver(basketItemsContainer, (entries) => {
  if (basketItemsCollapsible.value) {
    const entry = entries[0]
    const { height } = entry.contentRect

    const currentCollapsibleHeight: number = parseInt(basketItemsCollapsible.value.$el.style.getPropertyValue('--reka-collapsible-content-height'), 0)

    if (currentCollapsibleHeight !== height) {
      basketItemsCollapsible.value.$el.style.setProperty('--reka-collapsible-content-height', height)
    }
  }
})

defineExpose({ priceSum })
</script>
